import { getGetWpTokenQueryOptions, getSession, queryClient } from '@onesource/services';
import { injectPreloaderInTab, isSafari, openUrl } from '@onesource/utils';
import { useCallback } from 'react';

import { useFullscreenLoader } from '../fullscreen-loader/service';

type TUseClassicAreaLinkOptions = {
  /** Base URL of the legacy (WordPress) members site. Env-dependent, so each app passes its own. */
  wpMembersSite: string;
  /** Navigate the current tab instead of opening a new one. */
  self?: boolean;
};

/**
 * Sends the user to a page on the legacy site, signing them in through the WP SSO
 * `verify-token` endpoint so logged-out legacy sessions land on the right page.
 */
export const useClassicAreaLink = ({ wpMembersSite, self = false }: TUseClassicAreaLinkOptions) => {
  const { showLoader, hideLoader } = useFullscreenLoader();

  const getWpToken = useCallback(async (): Promise<string | undefined> => {
    const session = getSession();
    const payload = session ? JSON.parse(atob(session.split('.')[1])) : null;
    const payloadWpToken = payload?.wp_token as string | undefined;
    if (payloadWpToken) {
      return payloadWpToken;
    }
    const { token: wpToken } = await queryClient.ensureQueryData(getGetWpTokenQueryOptions({}));
    return wpToken;
  }, []);

  const handleClassicPanelClick = useCallback(
    async (redirectUrlParam?: string) => {
      const destination = redirectUrlParam || wpMembersSite;
      const redirectPath = destination.startsWith(wpMembersSite) ? destination : `${wpMembersSite}${destination}`;
      const loaderConfig = { title: 'Taking you to the Classic Area', subtitle: 'This will only take a moment...' };

      if (self || isSafari()) {
        showLoader(loaderConfig);
        try {
          const wpToken = await getWpToken();
          const fullAuthRedirectUrl = `${wpMembersSite}/verify-token?token=${wpToken}&redirect_url=${encodeURIComponent(redirectPath)}&wp_token=true`;
          openUrl(fullAuthRedirectUrl, { sameTab: true });
        } finally {
          hideLoader();
        }
        return;
      }

      const newTab = window.open('', '_blank');

      if (newTab) {
        injectPreloaderInTab(newTab);
      }

      try {
        showLoader(loaderConfig);

        const wpToken = await getWpToken();
        const verifyTokenUrl = `${wpMembersSite}/verify-token?token=${wpToken}&redirect_url=${encodeURIComponent(redirectPath)}&wp_token=true`;
        if (newTab) {
          newTab.location.href = verifyTokenUrl;
        }
      } catch {
        newTab?.close();
      } finally {
        hideLoader();
      }
    },
    [wpMembersSite, self, showLoader, hideLoader, getWpToken],
  );

  return { handleClassicPanelClick };
};
